import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  CustomButton,
  CustomeCkeckboxField,
  CustomNumberField,
  CustomSelectField,
  CustomTextField,
} from "../../../components";
import { Constant } from "../../../config";
import { Notify, Utils } from "../../../utils";
import { useAppSelector } from "../../../_app";
import { LeaveService } from "../../../_service";
import {
  ButtonContainer,
  DefaultEntitlementButton,
  DefaultEntitlementMessage,
  H3Heading,
} from "../AppSettingsStyles";
import cloneDeep from "lodash.clonedeep";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import styled from "styled-components";

const AddEntitlementPolicy = () => {
  const [isUseCustomName, setIsUseCustomName] = useState<boolean>(false);
  const [customName, setCustomName] = useState<string>("");
  const [isUseHoursWorked, setIsUseHoursWorked] = useState<boolean>(false);
  const [isUseUpfrontAccrual, setIsUseUpfrontAccrual] =
    useState<boolean>(false);
  const [isAllowLeaveToBeCarried, setIsAllowLeaveToBeCarried] =
    useState<boolean>(false);
  const [defaultEntitlement, setDefaultEntitlement] = useState<string>("");
  const [entitlementAvailableAfter, setEntitlementAvailableAfter] =
    useState<string>("0");
  const [leaveExpiresAfter, setLeaveExpiresAfter] = useState<string>("");
  const [useProRated, setUseProRated] = useState<boolean>(false);
  const [leaveForHours, setLeaveForHours] = useState<string>("");
  const [paidLeavesContribute, setPaidLeavesContribute] =
    useState<boolean>(false);
  const [limitType, setLimitType] = useState<string>("none");
  const [percentageBalance, setPercentageBalance] = useState<string>("");
  const [percentageEntitlement, setPercentageEntitlement] =
    useState<string>("0");
  const [numberOfDays, setNumberOfDays] = useState<string>("0");
  const [showRules, setShowRules] = useState<Boolean>(false);
  const [inputRows, setInputRows] = useState<any[]>([]);

  const { state, pathname } = useLocation();
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const leaveLimitTypes = [
    {
      key: "No Limit",
      value: "none",
    },
    {
      key: "Percentage of balance",
      value: "percentage_balance",
    },
    {
      key: "Percentage of entitlement",
      value: "percentage_entitlement",
    },
    {
      key: "Fixed Number of days",
      value: "fixed_days",
    },
  ];

  /**
   * Function to add Entitlement policy
   */
  const addEntitlementPolicy = async () => {
    try {
      const formData = new FormData();

      formData.append("name", state?.leaveName);
      formData.append("is_custom_name", isUseCustomName ? "true" : "false");
      formData.append("is_deletable", "true");
      if (isUseCustomName) {
        formData.append("custom_name", customName);
      }
      formData.append(
        "is_use_hours_worked_aacrual",
        isUseHoursWorked ? "true" : "false"
      );
      if (isUseHoursWorked) {
        formData.append("leave_for_every_hours_worked", leaveForHours);
        formData.append(
          "is_paid_leave_contribute",
          paidLeavesContribute ? "true" : "false"
        );
      } else {
        formData.append("deafult_entitlement", defaultEntitlement);
        formData.append(
          "entitlement_available_after",
          entitlementAvailableAfter
        );
        formData.append(
          "is_use_upfront_accrual",
          isUseUpfrontAccrual ? "true" : "false"
        );
        if (isUseUpfrontAccrual) {
          formData.append(
            "is_round_pro_rated_leave",
            useProRated ? "true" : "false"
          );
        }
      }
      formData.append(
        "is_allowed_carried_forward",
        isAllowLeaveToBeCarried ? "true" : "false"
      );
      formData.append(
        "cycle_rules", inputRows.length > 0 ? JSON.stringify(inputRows) : "[]"
      );
      if (isAllowLeaveToBeCarried) {
        formData.append(
          "carry_forward_leave_expires_after",
          String(leaveExpiresAfter)
        );
        formData.append("limit_type", limitType);
        if (limitType === "percentage_balance") {
          formData.append("percentage_of_balance", String(percentageBalance));
        } else if (limitType === "percentage_entitlement") {
          formData.append(
            "percentage_of_entitlement",
            String(percentageEntitlement)
          );
        } else if (limitType === "fixed_days") {
          formData.append("fixed_number_of_days", String(numberOfDays));
        }
      }
      if (pathname.split("/").pop() === "edit") {
        const { status } = await LeaveService._editEntitlementPolicy(
          formData,
          state?.entitlementPolicyDetails?.id
        );

        if (status === 200) {
          Notify("Entitlement Policy updated successfully", 1);
          navigate(-1);
        }
      } else {
        formData.append("leave_type", state?.leaveId);
        formData.append("company", user?.defaultCompnayId);
        const { status } = await LeaveService._addEntitlementPolicy(formData);

        if (status === 201) {
          Notify("Entitlement Policy added successfully");
          navigate(-1);
        }
      }
    } catch (error:any) {
      Utils.showErrorMessage(error)
    }
  };

  /**
   * Function to add Default Active Entitlement policy in case of new leave type
   */
  const addDefaultActiveEntitlementPolicy = async (
    entitlementPolicyId: number
  ) => {
    const formData = new FormData();
    formData.append("entitlement_policy", `${entitlementPolicyId}`);
    formData.append("company", user?.defaultCompnayId);
    formData.append("leave_type", state?.leaveId);

    try {
      const { status } = await LeaveService._addActiveEntitlementPolicy(
        formData
      );
      if (status !== 201) {
        Notify(Constant.common.something_went_wrong, 0);
      }
    } catch (error:any) {
      Utils.showErrorMessage(error);
    }
  };

  /**
   * Function to add Default Entitlement policy in case of new leave type
   */
  const addDefaultPolicy = async () => {
    if (state?.default) {
      try {
        const formData = new FormData();

        formData.append("leave_type", state?.leaveId);
        formData.append("company", user?.defaultCompnayId);
        formData.append("name", state?.leaveName);
        formData.append("is_custom_name", "true");
        formData.append("is_deletable", "true");
        formData.append("custom_name", "0.0 days");

        const { data, status } = await LeaveService._addEntitlementPolicy(
          formData
        );

        if (status === 201) {
          await addDefaultActiveEntitlementPolicy(data.data.id);
          navigate("/dashboard/app-settings/leave/view", {
            state: {
              leaveId: state?.leaveId,
            },
          });
        }
      } catch (error:any) {
        Utils.showErrorMessage(error);
      }
    }
  };

  console.log(state);
  /**
   * Set values to the states in case of edit Entitlement policy
   */
  useEffect(() => {
    console.log("theLocationState", state);
    if (state?.entitlementPolicyDetails) {
      setIsUseCustomName(
        () => state?.entitlementPolicyDetails?.is_custom_name || false
      );
      if(state?.entitlementPolicyDetails?.cycle_rules?.length > 0) {
        setShowRules(true);
        setInputRows(() => state?.entitlementPolicyDetails?.cycle_rules || []);
      }
      if (state?.entitlementPolicyDetails?.is_custom_name) {
        setCustomName(() => state?.entitlementPolicyDetails?.custom_name || "");
      }
      setIsUseHoursWorked(
        () =>
          state?.entitlementPolicyDetails?.is_use_hours_worked_aacrual || false
      );
      if (state?.entitlementPolicyDetails?.is_use_hours_worked_aacrual) {
        setLeaveForHours(
          () =>
            state?.entitlementPolicyDetails?.leave_for_every_hours_worked || "0"
        );
        setPaidLeavesContribute(
          () =>
            state?.entitlementPolicyDetails?.is_paid_leave_contribute || false
        );
      }
      setDefaultEntitlement(
        () => state?.entitlementPolicyDetails?.deafult_entitlement || "0"
      );
      setEntitlementAvailableAfter(
        () =>
          state?.entitlementPolicyDetails?.entitlement_available_after || "0"
      );
      setIsUseUpfrontAccrual(
        () => state?.entitlementPolicyDetails?.is_use_upfront_accrual || false
      );
      setUseProRated(
        () => state?.entitlementPolicyDetails?.is_round_pro_rated_leave || false
      );
      setIsAllowLeaveToBeCarried(
        () =>
          state?.entitlementPolicyDetails?.is_allowed_carried_forward || false
      );

      if (state?.entitlementPolicyDetails?.is_allowed_carried_forward) {
        setLeaveExpiresAfter(
          () =>
            state?.entitlementPolicyDetails
              ?.carry_forward_leave_expires_after || ""
        );
        setLimitType(() => state?.entitlementPolicyDetails?.limit_type);
        if (
          state?.entitlementPolicyDetails?.limit_type === "percentage_balance"
        ) {
          setPercentageBalance(
            () => state?.entitlementPolicyDetails?.percentage_of_balance || "0"
          );
        } else if (
          state?.entitlementPolicyDetails?.limit_type ===
          "percentage_entitlement"
        ) {
          setPercentageEntitlement(
            () =>
              state?.entitlementPolicyDetails?.percentage_of_entitlement || "0"
          );
        } else if (
          state?.entitlementPolicyDetails?.limit_type === "fixed_days"
        ) {
          setNumberOfDays(
            () => state?.entitlementPolicyDetails?.fixed_number_of_days || "0"
          );
        }
      }
    }
  }, []);

  const handleAddRule = () => {
    if(inputRows.length === 0) {
      setInputRows([{"first_cycle": '', "last_cycle": '', "entitlement": ''}])
    } else {
      let data = cloneDeep(inputRows);
      data.push({"first_cycle": '', "last_cycle": '', "entitlement": ''})
      setInputRows(data);
    }

    setShowRules(true);
  }

  console.log('inputRows', inputRows);

  return (
    <div>
      {state?.default && (
        <DefaultEntitlementMessage>
          This is a default Entitlement policy for the Leave type{" "}
          {state.leaveName}. If you want to edit it, please edit it in the form
          given below, if you dont want to change anything, click{" "}
          <DefaultEntitlementButton onClick={() => addDefaultPolicy()}>
            here
          </DefaultEntitlementButton>{" "}
          to create a default policy with 0 days
        </DefaultEntitlementMessage>
      )}
      <H3Heading>
        {!state?.entitlementPolicyDetails && "New "}Entitlement Policy
        {state?.entitlementPolicyDetails &&
          `: ${
            state.entitlementPolicyDetails?.custom_name ||
            state.entitlementPolicyDetails?.name
          }`}
      </H3Heading>
      <CustomTextField
        title={Constant.settings.entitlementPolicy.leave_type}
        value={state?.leaveName}
        disabled={true}
        onChangeText={() => {}}
      />
      <CustomeCkeckboxField
        title={Constant.settings.entitlementPolicy.custom_name}
        value={isUseCustomName}
        onChange={() => setIsUseCustomName(() => !isUseCustomName)}
      />
      {isUseCustomName && (
        <CustomTextField
          title={Constant.settings.entitlementPolicy.custom_name_detail}
          onChangeText={setCustomName}
          value={customName}
        />
      )}
      <CustomeCkeckboxField
        title={Constant.settings.entitlementPolicy.hours_worked}
        value={isUseHoursWorked}
        onChange={() => setIsUseHoursWorked(() => !isUseHoursWorked)}
      />
      {isUseHoursWorked ? (
        <>
          <CustomNumberField
            title={Constant.settings.entitlementPolicy.leave_for_hours}
            placehoder="0"
            value={leaveForHours}
            onChangeText={setLeaveForHours}
          />
          <CustomeCkeckboxField
            title={Constant.settings.entitlementPolicy.paid_leaves_contributes}
            value={paidLeavesContribute}
            onChange={() =>
              setPaidLeavesContribute(() => !paidLeavesContribute)
            }
          />
        </>
      ) : (
        <>
          <CustomNumberField
            title={Constant.settings.entitlementPolicy.default_entitlement}
            placehoder="0.0"
            value={defaultEntitlement}
            onChangeText={setDefaultEntitlement}
            isPositiveNumber={true}
            allowOneDecimalPlaceOnly={true}
            inputWidth="4rem"
          />
          <CustomNumberField
            title={Constant.settings.entitlementPolicy.entitlement_after}
            placehoder="0.0"
            value={entitlementAvailableAfter}
            onChangeText={setEntitlementAvailableAfter}
            isPositiveNumber={true}
            allowOneDecimalPlaceOnly={true}
            inputWidth="4rem"
          />
          <CustomeCkeckboxField
            title={Constant.settings.entitlementPolicy.upfront_accrual}
            value={isUseUpfrontAccrual}
            onChange={() => setIsUseUpfrontAccrual(() => !isUseUpfrontAccrual)}
          />
          {isUseUpfrontAccrual && (
            <CustomeCkeckboxField
              title={Constant.settings.entitlementPolicy.pro_rated}
              value={useProRated}
              onChange={() => setUseProRated(() => !useProRated)}
            />
          )}
          {isUseUpfrontAccrual && (
            <div style={{ margin: "0.5rem 0", color: "#6c757d", fontSize: "0.9rem", fontWeight: 500 }}>
              <p>If the Fraction of a day is less than one-half, round it down.</p>
              <p>If the Fraction of a day is half or more, round it up.</p>
            </div>
          )}
        </>
      )}

      <CustomeCkeckboxField
        title={Constant.settings.entitlementPolicy.leave_carried}
        value={isAllowLeaveToBeCarried}
        onChange={() =>
          setIsAllowLeaveToBeCarried(() => !isAllowLeaveToBeCarried)
        }
      />
      {isAllowLeaveToBeCarried && (
        <>
          <CustomNumberField
            title={
              Constant.settings.entitlementPolicy.carried_fwd_leave_expires
            }
            value={leaveExpiresAfter}
            onChangeText={setLeaveExpiresAfter}
            isPositiveNumber={true}
            noDecimalValue={true}
            placehoder="0"
            inputWidth="4rem"
          />
          <CustomSelectField
            title={Constant.settings.entitlementPolicy.limit_type}
            value={limitType}
            onChangeOption={(value) => setLimitType(() => value)}
            option={leaveLimitTypes?.map((option) => ({
              key: option.key,
              value: option.value,
            }))}
          />
          {limitType === "percentage_balance" && (
            <CustomNumberField
              title={Constant.settings.entitlementPolicy.percentage_balance}
              placehoder="0"
              value={percentageBalance}
              onChangeText={setPercentageBalance}
            />
          )}
          {limitType === "percentage_entitlement" && (
            <CustomNumberField
              title={Constant.settings.entitlementPolicy.percentage_entitlement}
              placehoder="0"
              value={percentageEntitlement}
              onChangeText={setPercentageEntitlement}
            />
          )}
          {limitType === "fixed_days" && (
            <CustomNumberField
              title={Constant.settings.entitlementPolicy.number_days}
              placehoder="0"
              value={numberOfDays}
              onChangeText={setNumberOfDays}
            />
          )}
        </>
      )}

      {/* Add the Cycle stuff here */}

      <div
        style={{
          display: "flex",
          // justifyContent: "space-between",
          // width: "34%",
          margin: "1rem 0",
          alignItems: "center",
        }}
      >
        <p
          style={{
            color: "#495057",
            fontSize: "1rem",
            fontWeight: 700,
            marginRight: "7rem",
          }}
        >
          Cycle Specific Rules
        </p>
        <div
          style={{
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            padding: "0.25rem 0.5rem",
            background: "rgb(3, 179, 129)",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "1rem",
            borderRadius: "0.3125rem",
            width: "fit-content",
            cursor: "pointer",
          }}
          onClick={handleAddRule}
        >
          <FaPlus size={20} />
        </div>
      </div>

      {showRules && (
        <div
          style={{
        width: "80%",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "1rem",
        marginBottom: "1rem",
        alignItems: "center"
          }}
        >
          {["First Cycle", "Last Cycle", "Entitlement", ""].map((x: string) => (
        <p style={{ 
          fontWeight: 600, 
          fontSize: "0.9rem",
          color: "#495057",
          margin: 0,
          textAlign: 'center'
        }}>{x}</p>
          ))}
        </div>
      )}
      {showRules &&
        inputRows.map((x: any, rowIndex) => (
          <div
        key={rowIndex}
        style={{
          width: "80%",
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "1rem",
          marginBottom: "0.75rem",
          alignItems: "center"
        }}
          >
        {["first_cycle","last_cycle","entitlement", ""].map((z, index) => (
            <div key={index}>
            {index !== 3 ? (
              <CustomInput
              type="number" 
              value={x[z]}
              onChange={(e) => {
              const value = e.target.value;
              // Allow empty string or valid positive numbers with up to 1 decimal place
              if (value === '' || /^\d+(\.\d?)?$/.test(value)) {
                const newRows = [...inputRows];
                newRows[rowIndex][z] = Number(value);
                setInputRows(newRows);
              }
              }}
              min="0"
              step="0.1"
              style={{
              width: "100%",
              padding: "0.5rem",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              fontSize: "0.9rem"
              }} 
              />
            ) : (
              <RiDeleteBin6Line
              size={24} 
              color="#ff6665"
              style={{cursor: 'pointer'}}
              onClick={() => {
              const newRows = inputRows.filter((_, i) => i !== rowIndex);
              setInputRows(newRows);
              if (newRows.length === 0) setShowRules(false);
              }}
              />
            )}
            </div>
        ))}
          </div>
        ))}

      <ButtonContainer>
        <CustomButton
          title={Constant.common.cancel}
          type="secondary"
          onClick={() => navigate(-1)}
        />
        <CustomButton
          title={Constant.common.save}
          onClick={addEntitlementPolicy}
        />
      </ButtonContainer>
    </div>
  );
};

export default AddEntitlementPolicy;


const CustomInput = styled.input<any>`
  width: ${(props) => props.width};
  border-radius: 0.2rem;
  -webkit-appearance: none;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }

  &:disabled {
    background: #e6e6e6;
  }
`;